import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';

import axios from 'axios';
import {
  useNavigate,
  useParams
} from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import {
  FaArrowLeft,
  FaPaperPlane,
  FaTrash
} from 'react-icons/fa';

import '../css/MomentComments.css';

function MomentComments() {
  const { momentId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [comments, setComments] = useState([]);
  const [content, setContent] = useState('');

  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [errorMessage, setErrorMessage] = useState('');

  const token = localStorage.getItem('authToken');

  const authorizationHeader = useMemo(() => {
    if (!token) return null;

    return token.startsWith('Bearer ')
      ? token
      : `Bearer ${token}`;
  }, [token]);

  const api = useMemo(() => {
    return axios.create({
      baseURL: process.env.REACT_APP_API_URL,
      headers: authorizationHeader
        ? {
            Authorization: authorizationHeader
          }
        : {}
    });
  }, [authorizationHeader]);

  const fetchComments = useCallback(
    async (pageNumber = 0, append = false) => {
      if (!authorizationHeader) {
        setErrorMessage(
          t(
            'moments.authenticationRequired',
            'Vous devez être connecté.'
          )
        );
        setLoading(false);
        setLoadingMore(false);
        return;
      }

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      setErrorMessage('');

      try {
        const response = await api.get(
          `/api/moments/${momentId}/comments`,
          {
            params: {
              page: pageNumber,
              size: 20
            }
          }
        );

        const payload = response.data;
        const content = Array.isArray(payload?.content)
          ? payload.content
          : Array.isArray(payload)
          ? payload
          : [];

        setComments((previous) => {
          if (!append) return content;

          const existingIds = new Set(
            previous.map((comment) => comment.id)
          );

          return [
            ...previous,
            ...content.filter(
              (comment) => !existingIds.has(comment.id)
            )
          ];
        });

        setPage(pageNumber);

        if (typeof payload?.last === 'boolean') {
          setHasMore(!payload.last);
        } else {
          setHasMore(content.length === 20);
        }
      } catch (error) {
        console.error(
          '❌ Fetch Moment comments failed:',
          error
        );

        setErrorMessage(
          error.response?.data?.message ||
            t(
              'moments.commentsLoadError',
              'Impossible de charger les commentaires.'
            )
        );
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [api, authorizationHeader, momentId, t]
  );

  useEffect(() => {
    document.body.classList.add('moment-comments-body');

    fetchComments(0, false);

    return () => {
      document.body.classList.remove(
        'moment-comments-body'
      );
    };
  }, [fetchComments]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const trimmedContent = content.trim();

    if (!trimmedContent) return;

    if (trimmedContent.length > 500) {
      setErrorMessage(
        t(
          'moments.commentTooLong',
          'Le commentaire ne peut pas dépasser 500 caractères.'
        )
      );
      return;
    }

    setSubmitting(true);
    setErrorMessage('');

    try {
      const response = await api.post(
        `/api/moments/${momentId}/comments`,
        {
          content: trimmedContent
        }
      );

      setComments((previous) => [
        response.data,
        ...previous
      ]);

      setContent('');

      window.dispatchEvent(
        new Event('refresh-moments')
      );
    } catch (error) {
      console.error(
        '❌ Add Moment comment failed:',
        error
      );

      setErrorMessage(
        error.response?.data?.message ||
          t(
            'moments.commentCreateError',
            'Impossible d’ajouter ce commentaire.'
          )
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    const confirmed = window.confirm(
      t(
        'moments.deleteCommentConfirm',
        'Supprimer ce commentaire ?'
      )
    );

    if (!confirmed) return;

    try {
      await api.delete(
        `/api/moments/${momentId}/comments/${commentId}`
      );

      setComments((previous) =>
        previous.filter(
          (comment) => comment.id !== commentId
        )
      );

      window.dispatchEvent(
        new Event('refresh-moments')
      );
    } catch (error) {
      console.error(
        '❌ Delete Moment comment failed:',
        error
      );

      window.alert(
        error.response?.data?.message ||
          t(
            'moments.commentDeleteError',
            'Impossible de supprimer ce commentaire.'
          )
      );
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return '';

    const date = new Date(dateValue);
    const seconds = Math.max(
      1,
      Math.floor(
        (Date.now() - date.getTime()) / 1000
      )
    );

    if (seconds < 60) {
      return t(
        'moments.justNow',
        'À l’instant'
      );
    }

    const minutes = Math.floor(seconds / 60);

    if (minutes < 60) {
      return t('moments.minutesAgo', {
        defaultValue: 'Il y a {{count}} min',
        count: minutes
      });
    }

    const hours = Math.floor(minutes / 60);

    if (hours < 24) {
      return t('moments.hoursAgo', {
        defaultValue: 'Il y a {{count}} h',
        count: hours
      });
    }

    const days = Math.floor(hours / 24);

    return t('moments.daysAgo', {
      defaultValue: 'Il y a {{count}} j',
      count: days
    });
  };

  if (loading) {
    return (
      <main className="moment-comments-page">
        <div className="moment-comments-loading">
          <div />
          <p>
            {t(
              'moments.loadingComments',
              'Chargement des commentaires...'
            )}
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="moment-comments-page">
      <section className="moment-comments-shell">
        <header className="moment-comments-header">
          <button
            type="button"
            className="moment-comments-back"
            onClick={() => navigate(-1)}
            aria-label={t(
              'common.back',
              'Retour'
            )}
          >
            <FaArrowLeft />
          </button>

          <div>
            <h1>
              {t(
                'moments.commentsTitle',
                'Commentaires'
              )}
            </h1>

            <p>
              {comments.length}{' '}
              {t(
                'moments.commentCount',
                'commentaire(s)'
              )}
            </p>
          </div>
        </header>

        {errorMessage && (
          <div
            className="moment-comments-error"
            role="alert"
          >
            {errorMessage}
          </div>
        )}

        <div className="moment-comments-list">
          {comments.length === 0 ? (
            <div className="moment-comments-empty">
              <h2>
                {t(
                  'moments.noComments',
                  'Aucun commentaire'
                )}
              </h2>

              <p>
                {t(
                  'moments.beFirstComment',
                  'Soyez la première personne à commenter ce Moment.'
                )}
              </p>
            </div>
          ) : (
            comments.map((comment) => (
              <article
                key={comment.id}
                className="moment-comments-item"
              >
                <img
                  src={
                    comment.userProfilePhoto ||
                    '/default-profile.png'
                  }
                  alt={
                    comment.userName ||
                    'User'
                  }
                  onError={(event) => {
                    event.currentTarget.src =
                      '/default-profile.png';
                  }}
                />

                <div className="moment-comments-content">
                  <div className="moment-comments-meta">
                    <strong>
                      {comment.userName ||
                        t(
                          'moments.user',
                          'Utilisateur'
                        )}
                    </strong>

                    <span>
                      {formatDate(
                        comment.createdAt
                      )}
                    </span>
                  </div>

                  <p>
                    {comment.content}
                  </p>
                </div>

                {comment.ownedByCurrentUser && (
                  <button
                    type="button"
                    className="moment-comments-delete"
                    onClick={() =>
                      handleDelete(comment.id)
                    }
                    aria-label={t(
                      'common.delete',
                      'Supprimer'
                    )}
                  >
                    <FaTrash />
                  </button>
                )}
              </article>
            ))
          )}
        </div>

        {hasMore && comments.length > 0 && (
          <button
            type="button"
            className="moment-comments-load-more"
            onClick={() =>
              fetchComments(page + 1, true)
            }
            disabled={loadingMore}
          >
            {loadingMore
              ? t(
                  'moments.loadingMore',
                  'Chargement...'
                )
              : t(
                  'moments.loadMoreComments',
                  'Voir plus de commentaires'
                )}
          </button>
        )}
      </section>

      <form
        className="moment-comments-form"
        onSubmit={handleSubmit}
      >
        <div className="moment-comments-form-inner">
          <textarea
            value={content}
            onChange={(event) =>
              setContent(event.target.value)
            }
            maxLength={500}
            rows="1"
            placeholder={t(
              'moments.commentPlaceholder',
              'Ajouter un commentaire...'
            )}
          />

          <span>
            {content.length}/500
          </span>

          <button
            type="submit"
            disabled={
              submitting ||
              !content.trim()
            }
            aria-label={t(
              'moments.sendComment',
              'Envoyer'
            )}
          >
            <FaPaperPlane />
          </button>
        </div>
      </form>
    </main>
  );
}

export default MomentComments;